import { useMemo } from "react";
import type { BarChartConfig } from "@store/slices/chart-config-slice";
import type { QuestionResponsesSearchResponse } from "@models/Filter";
import type { TimePeriod } from "@store/slices/crosstab-config-slice";
import {
  getTimePeriodFooterText,
  getTimePeriodNominalRepresentation,
} from "@utils/time-period-display";
import {
  getRollingAverageFooterText,
  getRollingAverageComparisonFooterText,
  getMissingDataWarning,
  getPeriodTypeLabel,
} from "@utils/rolling-average-utils";
import {
  resolveTimePeriodInWindow,
  getTimePeriodDates,
} from "@utils/time-period-utils";
import {
  processGroupedBarChartData,
  normalizeGroupedCell,
  type GroupedBarCell,
} from "@utils/grouped-bar-utils";

interface ViewerState {
  rollingAverage: number | null;
  timeUnit: string | null;
  timePeriod: string | null;
}

interface UseGroupedBarFooterOptions {
  weightsApplied?: boolean;
  hasCustomTitle?: boolean | null;
  dashboardDateOverride?: { startDate: string | null; endDate: string | null };
  viewerState?: ViewerState;
  dataAvailabilityDate?: string | null;
}

export interface GroupedBarFooterStatTestingNote {
  hasSignificantChanges: boolean;
  significanceLevel: string;
  comparisonLabel: string;
  comparisonDescription: string;
}

export interface GroupedBarFooterData {
  dashboardFilterNote: string | null;
  timePeriodFooterText: string | null;
  rollingAverageFooterText: string | null;
  rollingAverageComparisonFooterText: string | null;
  missingDataWarning: string | null;
  isStatTestingSuppressed: boolean;
  weight: string;
  weightsApplied: boolean;
  showChartN: boolean;
  chartNValue: number | null;
  baseName: string | null;
  comparisonRollingLabel: string | null;
  rollingPeriodsWarningMessage: string | null;
  statTestingNote: GroupedBarFooterStatTestingNote | null;
}

/**
 * Derives the grouped bar chart footer content (weight, time period, rolling
 * average, visualization base, and statistical-testing context) from the saved
 * config and the currently rendered response, following the same conventions as
 * the Bar Chart footer. Kept separate from BarChart so the two can diverge.
 */
export function useGroupedBarFooter(
  config: BarChartConfig | undefined,
  filteredData: QuestionResponsesSearchResponse | null | undefined,
  {
    weightsApplied = true,
    hasCustomTitle,
    dashboardDateOverride,
    viewerState,
    dataAvailabilityDate,
  }: UseGroupedBarFooterOptions,
): GroupedBarFooterData {
  const isStatTestingEnabled = config?.statisticalTesting?.enabled || false;

  const timePeriodFooterText = useMemo(() => {
    const viewerTP = viewerState?.timePeriod ?? null;
    const savedTP = config?.timePeriodSelection?.timePeriod;
    const effectivePeriod = viewerTP ?? savedTP;

    if (!effectivePeriod || effectivePeriod === "all_time") return null;
    if (!hasCustomTitle) return null;

    if (viewerTP && viewerTP !== savedTP) {
      const resolved = getTimePeriodDates(
        viewerTP,
        dataAvailabilityDate ?? null,
      );
      return getTimePeriodFooterText(
        viewerTP as TimePeriod,
        resolved?.startDate,
        resolved?.endDate,
      );
    }

    return getTimePeriodFooterText(
      effectivePeriod as TimePeriod,
      config?.timePeriodSelection?.startDate,
      config?.timePeriodSelection?.endDate,
    );
  }, [
    viewerState?.timePeriod,
    config?.timePeriodSelection,
    hasCustomTitle,
    dataAvailabilityDate,
  ]);

  const dashboardFilterNote = useMemo(() => {
    if (!dashboardDateOverride?.startDate || !dashboardDateOverride?.endDate) {
      return null;
    }
    const effectiveTimePeriod = (viewerState?.timePeriod ??
      config?.timePeriodSelection?.timePeriod) as TimePeriod | undefined;
    if (
      !effectiveTimePeriod ||
      effectiveTimePeriod === "all_time" ||
      effectiveTimePeriod === "custom_period"
    ) {
      return `Time Period (filtered): ${dashboardDateOverride.startDate} – ${dashboardDateOverride.endDate}`;
    }
    const effectiveEnd =
      dataAvailabilityDate &&
      dataAvailabilityDate < dashboardDateOverride.endDate
        ? dataAvailabilityDate
        : dashboardDateOverride.endDate;
    const resolved = resolveTimePeriodInWindow(
      effectiveTimePeriod,
      dashboardDateOverride.startDate,
      effectiveEnd,
    );
    if (!resolved) {
      const unit = effectiveTimePeriod.includes("semester")
        ? "semester"
        : "quarter";
      return `No complete ${unit} available within the selected date range.`;
    }
    const label = getTimePeriodNominalRepresentation(
      effectiveTimePeriod,
      resolved.startDate,
      resolved.endDate,
    );

    const viewerRA = viewerState?.rollingAverage ?? null;
    const viewerExplicitlyDisabledRA = viewerRA === 0;
    const hasViewerRA = viewerRA !== null && viewerRA !== 0;
    const savedRAEnabled =
      !!config?.rollingAverage?.enabled && !!config?.rollingAverage?.windowSize;
    const effectiveRAEnabled =
      !viewerExplicitlyDisabledRA && (hasViewerRA || savedRAEnabled);

    if (effectiveRAEnabled) {
      const windowSize = hasViewerRA
        ? (viewerRA as number)
        : (config?.rollingAverage?.windowSize as number);
      const rollingText = getRollingAverageFooterText(
        windowSize,
        effectiveTimePeriod,
        resolved.startDate,
        resolved.endDate,
      );
      return rollingText.replace(
        "Rolling Average:",
        "Rolling Average (filtered):",
      );
    }

    if (isStatTestingEnabled) {
      const periodLabels: Partial<Record<TimePeriod, string>> = {
        latest_quarter: "Latest Quarter",
        previous_quarter: "Previous Quarter",
        latest_semester: "Latest Semester",
        previous_semester: "Previous Semester",
      };
      const periodLabel = periodLabels[effectiveTimePeriod];

      if (periodLabel) {
        const dayBefore = new Date(resolved.startDate);
        dayBefore.setDate(dayBefore.getDate() - 1);
        const dayBeforeStr = dayBefore.toISOString().split("T")[0];

        const comparisonType = (
          effectiveTimePeriod.includes("semester")
            ? "latest_semester"
            : "latest_quarter"
        ) as TimePeriod;
        const comparisonResolved = resolveTimePeriodInWindow(
          comparisonType,
          "2000-01-01",
          dayBeforeStr,
        );

        if (comparisonResolved) {
          const comparisonLabel = getTimePeriodNominalRepresentation(
            comparisonType,
            comparisonResolved.startDate,
            comparisonResolved.endDate,
          );
          if (comparisonLabel) {
            return `${periodLabel} (filtered): ${label} vs ${comparisonLabel}`;
          }
        }
      }
    }

    return label ? `Time Period (filtered): ${label}` : null;
  }, [
    dashboardDateOverride,
    config?.timePeriodSelection?.timePeriod,
    config?.rollingAverage,
    isStatTestingEnabled,
    viewerState?.timePeriod,
    viewerState?.rollingAverage,
    dataAvailabilityDate,
  ]);

  const isStatTestingSuppressed =
    !!dashboardFilterNote?.startsWith("No complete");

  const rollingAverageFooterText = useMemo(() => {
    const viewerRA = viewerState?.rollingAverage ?? null;
    const viewerTP = viewerState?.timePeriod ?? null;
    const hasViewerRA = viewerRA !== null && viewerRA !== 0;
    const viewerExplicitlyDisabledRA = viewerRA === 0;
    const savedRAEnabled =
      config?.rollingAverage?.enabled && config.rollingAverage.windowSize;

    if (viewerExplicitlyDisabledRA) return null;

    if (!hasViewerRA && (!savedRAEnabled || !hasCustomTitle)) {
      return null;
    }

    const savedTP = config?.timePeriodSelection?.timePeriod;
    const timePeriod = (viewerTP ?? savedTP) as TimePeriod | undefined;
    if (!timePeriod) return null;

    const effectiveWindowSize = hasViewerRA
      ? viewerRA
      : (config?.rollingAverage?.windowSize as number);

    if (viewerTP && viewerTP !== savedTP) {
      const resolved = getTimePeriodDates(
        viewerTP,
        dataAvailabilityDate ?? null,
      );
      return getRollingAverageFooterText(
        effectiveWindowSize,
        timePeriod,
        resolved?.startDate,
        resolved?.endDate,
      );
    }

    return getRollingAverageFooterText(
      effectiveWindowSize,
      timePeriod,
      config?.timePeriodSelection?.startDate,
      config?.timePeriodSelection?.endDate,
    );
  }, [
    viewerState?.rollingAverage,
    viewerState?.timePeriod,
    config?.rollingAverage,
    config?.timePeriodSelection?.timePeriod,
    config?.timePeriodSelection?.startDate,
    config?.timePeriodSelection?.endDate,
    hasCustomTitle,
    dataAvailabilityDate,
  ]);

  const rollingAverageComparisonFooterText = useMemo(() => {
    const viewerRA = viewerState?.rollingAverage ?? null;
    const viewerTP = viewerState?.timePeriod ?? null;
    const hasViewerRA = viewerRA !== null && viewerRA !== 0;
    const viewerExplicitlyDisabledRA = viewerRA === 0;

    if (viewerExplicitlyDisabledRA) return null;

    const savedRAEnabled =
      !!config?.rollingAverage?.enabled && !!config?.rollingAverage?.windowSize;

    if (!hasViewerRA && !savedRAEnabled) return null;

    const savedTP = config?.timePeriodSelection?.timePeriod;
    const timePeriod = (viewerTP ?? savedTP) as TimePeriod | undefined;
    if (!timePeriod) return null;

    const effectiveWindowSize = hasViewerRA
      ? (viewerRA as number)
      : (config?.rollingAverage?.windowSize as number);

    if (viewerTP && viewerTP !== savedTP) {
      const resolved = getTimePeriodDates(
        viewerTP,
        dataAvailabilityDate ?? null,
      );
      return getRollingAverageComparisonFooterText(
        effectiveWindowSize,
        timePeriod,
        resolved?.startDate,
        resolved?.endDate,
      );
    }

    return getRollingAverageComparisonFooterText(
      effectiveWindowSize,
      timePeriod,
      config?.timePeriodSelection?.startDate,
      config?.timePeriodSelection?.endDate,
    );
  }, [
    viewerState?.rollingAverage,
    viewerState?.timePeriod,
    config?.rollingAverage,
    config?.timePeriodSelection?.timePeriod,
    config?.timePeriodSelection?.startDate,
    config?.timePeriodSelection?.endDate,
    dataAvailabilityDate,
  ]);

  const missingDataWarning = useMemo(() => {
    if (!config?.rollingAverage?.enabled || !config.rollingAverage.windowSize) {
      return null;
    }

    const timePeriod = config?.timePeriodSelection?.timePeriod;
    if (!timePeriod) return null;

    return getMissingDataWarning(
      config.rollingAverage.windowSize,
      config?.effectiveRollingPeriodCount,
      config?.missingRollingPeriodLabels,
    );
  }, [
    config?.rollingAverage,
    config?.effectiveRollingPeriodCount,
    config?.missingRollingPeriodLabels,
    config?.timePeriodSelection?.timePeriod,
  ]);

  const showChartN = config?.showChartN ?? false;
  const chartNValue = useMemo(() => {
    if (!showChartN) return null;
    if (filteredData) {
      const baseNValues = (filteredData.data ?? [])
        .map(
          (item) =>
            (item.surveys?.[0]?.descriptiveMetrics?.[0] as { baseN?: number })
              ?.baseN,
        )
        .filter((n): n is number => typeof n === "number");
      if (baseNValues.length > 0) return Math.max(...baseNValues);
      return filteredData.question?.responseCount ?? config?.chartN ?? null;
    }
    return config?.chartN ?? null;
  }, [showChartN, filteredData, config?.chartN]);

  const statTestingNote = useMemo((): GroupedBarFooterStatTestingNote | null => {
    if (!isStatTestingEnabled || isStatTestingSuppressed) return null;

    // Derive from the same grouped cells the markers render from, so the note
    // can never disagree with the on-bar arrows. Scan every series/cell (not
    // just descriptiveMetrics[0], which for grouped bar is only the first
    // series). Live response wins; fall back to the stored grouped chartData.
    const cellIsSignificant = (cell: GroupedBarCell): boolean =>
      cell.statSignificance !== 0 && cell.statSignificance !== undefined;

    const hasSignificantChanges = (() => {
      if (filteredData?.data) {
        const grouped = processGroupedBarChartData(filteredData.data);
        return grouped.series.some((s) =>
          s.data.some((cell) => cellIsSignificant(normalizeGroupedCell(cell))),
        );
      }
      const storedSeries = config?.groupedBar?.chartData?.series;
      if (storedSeries) {
        return storedSeries.some((s) =>
          s.data.some((cell) => cellIsSignificant(normalizeGroupedCell(cell))),
        );
      }
      return false;
    })();

    const significanceLevel =
      config?.statisticalTesting?.significanceLevel || "90%";
    const isRollingAverage =
      config?.rollingAverage?.enabled &&
      config.rollingAverage.windowSize !== undefined &&
      config.rollingAverage.windowSize > 0;
    const windowSize =
      config?.effectiveRollingPeriodCount ?? config?.rollingAverage?.windowSize;
    const periodType = config?.timePeriodSelection?.timePeriod
      ? getPeriodTypeLabel(config.timePeriodSelection.timePeriod)
      : "period";
    const comparisonLabel = isRollingAverage
      ? "Previous Rolling Window"
      : "Previous Period";
    const comparisonDescription = isRollingAverage
      ? `previous overlapping ${windowSize}-${periodType} rolling average`
      : "previous period";

    return {
      hasSignificantChanges,
      significanceLevel,
      comparisonLabel,
      comparisonDescription,
    };
  }, [
    isStatTestingEnabled,
    isStatTestingSuppressed,
    filteredData,
    config?.groupedBar?.chartData,
    config?.statisticalTesting?.significanceLevel,
    config?.rollingAverage,
    config?.effectiveRollingPeriodCount,
    config?.timePeriodSelection?.timePeriod,
  ]);

  return {
    dashboardFilterNote,
    timePeriodFooterText,
    rollingAverageFooterText,
    rollingAverageComparisonFooterText,
    missingDataWarning,
    isStatTestingSuppressed,
    weight: config?.weight || "DISABLED",
    weightsApplied,
    showChartN,
    chartNValue,
    baseName:
      config?.visualizationBase?.enabled && config.visualizationBase.name
        ? config.visualizationBase.name
        : null,
    comparisonRollingLabel:
      config?.rollingAverage?.enabled && config?.comparisonRollingLabel
        ? config.comparisonRollingLabel
        : null,
    rollingPeriodsWarningMessage: config?.rollingPeriodsWarningMessage ?? null,
    statTestingNote,
  };
}
